import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormHelper } from '@common/form-helper';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { pairwise, startWith, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-upgrade-dosage-upsell',
  imports: [ReactiveFormsModule],
  templateUrl: './upgrade-dosage-upsell.component.html',
  styleUrls: ['./upgrade-dosage-upsell.component.scss'],
})
export class UpgradeDosageUpsellComponent extends FormHelper implements OnInit, OnDestroy {
  @Input() public qaIdPrefix: string = 'Dosage Upgrade';
  @Input() public form: FormGroup;

  public defaultForm = new FormGroup({
    upgradeDosage: new FormControl('', Validators.required),
  });

  protected destroy$ = new Subject<void>();

  protected consultationStorageService = inject(ConsultationStorageService);
  private consultationRequestService = inject(ConsultationRequestService);

  /**
   * Get the form control for the upgrade dosage
   */
  private get upgradeDosageControl(): FormControl {
    return this.form.get('upgradeDosage') as FormControl;
  }

  /**
   * Initialize the component
   */
  public ngOnInit() {
    if (!this.form) {
      this.form = this.defaultForm;
    }
    this.setupForm();
  }

  /**
   * Clean up subscriptions when the component is destroyed
   */
  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup form subscriptions and prefill
   */
  protected setupForm(): void {
    this.subscribeToFormChanges();
    this.prefillUpgradeDosage();
  }

  /**
   * Subscribe to the form changes and handle the upgrade dosage selection
   */
  protected subscribeToFormChanges(): void {
    this.form.valueChanges
      .pipe(startWith(this.form.value), pairwise(), takeUntil(this.destroy$))
      .subscribe(([previousValue, currentValue]) => {
        this.removePreviousUpgradeUpsell(previousValue.upgradeDosage);
        this.handleUpgradeDosageSelected(currentValue.upgradeDosage);
      });
  }

  /**
   * Prefill the upgrade dosage if it exists in local storage
   */
  protected prefillUpgradeDosage(): void {
    const upgradeDosage = this.consultationStorageService.upsellUpgrades['upgradeDosage'];

    if (upgradeDosage) {
      this.upgradeDosageControl.patchValue(upgradeDosage);
    }
  }

  /**
   * Handle the selection of the upgrade dosage
   *
   * @param upgradeDosageValue {string} The selected upgrade dosage value
   */
  protected handleUpgradeDosageSelected(upgradeDosageValue: string): void {
    this.setTreatmentPreference(upgradeDosageValue);
    this.setDosageUpsellUpgradeAnswer(upgradeDosageValue);
    this.setUpsells(upgradeDosageValue);
  }

  /**
   * Sets the treatment preference based on the dosage form selected option.
   * Only sets the preference if user upgrades; otherwise lets the service determine it at submit.
   */
  protected setTreatmentPreference(upgradeDosageValue: string): void {
    if (!upgradeDosageValue) {
      return;
    }

    if (upgradeDosageValue === QuestionnaireAnswers.No) {
      this.consultationStorageService.treatmentPreferences =
        this.consultationRequestService.getOrderedTreatmentPreferences();

      return;
    }

    this.consultationStorageService.treatmentPreferences = upgradeDosageValue;
  }

  /**
   * Sets the dosage upsell upgrade answer in local storage.
   *
   * @param upgradeDosageValue {string} The selected upgrade dosage value
   */
  protected setDosageUpsellUpgradeAnswer(upgradeDosageValue: string): void {
    const upsellUpgrades = this.consultationStorageService.upsellUpgrades;
    upsellUpgrades['upgradeDosage'] = upgradeDosageValue;
    this.consultationStorageService.upsellUpgrades = upsellUpgrades;
  }

  /**
   * Removes a previously selected upgrade dosage from the upsells list.
   *
   * @param {string} previousUpgradeDosageValue the upgrade dosage value to be removed from upsells
   */
  private removePreviousUpgradeUpsell(previousUpgradeDosageValue: string): void {
    this.consultationStorageService.upsells = this.consultationStorageService.upsells.filter(
      (upsell) => upsell !== previousUpgradeDosageValue,
    );
  }

  /**
   * Adds an upgrade dosage value to the upsells list.
   *
   * Validates that the upgrade value is a valid UpsellSlug before adding it to the upsells array.
   *
   * @param {string} upgradeDosageValue the upgrade dosage value to be added to upsells
   */
  private setUpsells(upgradeDosageValue: string): void {
    if (!(<string[]>Object.values(UpsellSlugs)).includes(upgradeDosageValue)) {
      return;
    }

    this.consultationStorageService.upsells = [...this.consultationStorageService.upsells, upgradeDosageValue];
  }
}
